import React from "react";
import { useTranslation } from "react-i18next";

function getSparklinePath(tone, count) {
  const safeCount = Math.max(0, Number(count) || 0);

  if (safeCount === 0) {
    return (
      "M8 42C30 42 48 42 68 42" +
      "C92 42 116 42 138 42" +
      "C160 42 186 42 212 42"
    );
  }

  if (safeCount <= 2) {
    return {
      linkedCases:
        "M8 42C20 41 34 40 50 39" +
        "C68 38 86 37.5 106 37" +
        "C128 36.5 150 36 174 35.5" +
        "C190 35.2 202 35 212 34.8",

      manualCases:
        "M8 42C22 41.4 36 40.8 52 40" +
        "C72 39 94 38.3 116 37.7" +
        "C138 37.1 160 36.6 182 36.1" +
        "C196 35.8 206 35.5 212 35.2",

      messages:
        "M8 42C20 41.3 34 40.4 50 39.6" +
        "C70 38.8 90 38 112 37.3" +
        "C134 36.7 156 36.2 178 35.8" +
        "C194 35.5 204 35.2 212 35",
    }[tone];
  }

  if (safeCount <= 5) {
    return {
      linkedCases:
        "M8 42C18 40 30 37.6 44 35.5" +
        "C60 33.2 78 32 98 31.2" +
        "C120 30.4 144 30.1 168 29.8" +
        "C188 29.5 202 29.2 212 29",

      manualCases:
        "M8 42C20 40.8 34 39.2 50 37.5" +
        "C70 35.6 92 34.2 116 33.1" +
        "C140 32 162 31.2 184 30.5" +
        "C198 30.1 206 29.8 212 29.5",

      messages:
        "M8 42C18 40.4 30 38.6 46 36.8" +
        "C64 34.8 84 33.5 106 32.4" +
        "C128 31.4 150 30.7 174 30.1" +
        "C192 29.7 204 29.4 212 29.2",
    }[tone];
  }

  return {
    linkedCases:
      "M8 42C16 38 26 33 40 28" +
      "C56 23.5 76 20.5 100 18.5" +
      "C126 16.5 152 15.5 178 14.5" +
      "C194 13.9 204 13.4 212 13",

    manualCases:
      "M8 42C18 39 30 35.5 46 31.8" +
      "C66 27.5 90 24.6 116 22.7" +
      "C142 20.9 166 19.5 188 18.2" +
      "C200 17.5 208 17 212 16.8",

    messages:
      "M8 42C16 38.5 28 34.2 42 30" +
      "C60 25.5 82 22.5 108 20.3" +
      "C134 18.2 158 16.8 182 15.6" +
      "C196 14.9 206 14.3 212 14",
  }[tone];
}

function AnimatedSparkline({
  tone = "linkedCases",
  count = 0,
  isEmpty = true,
}) {
  const lineClass = [
    "lender-dashboard__sparkline-line",
    `lender-dashboard__sparkline-line--${tone}`,
    isEmpty
      ? "lender-dashboard__sparkline-line--empty"
      : "lender-dashboard__sparkline-line--active",
  ].join(" ");

  const path = getSparklinePath(tone, count);

  return (
    <svg
      className="lender-dashboard__sparkline"
      viewBox="0 0 220 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <path
        d="M8 42H212"
        className="lender-dashboard__sparkline-base"
        strokeWidth="1.6"
        strokeLinecap="round"
      />

      <path
        d={path}
        className={lineClass}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength="100"
      />
    </svg>
  );
}

export default function SummaryCard({
  type = "linkedCases",
  count = 0,
  label = "",
  helperCount = 0,
  helperType = "week",
  isLoading = false,
  delayClass = "",
}) {
  const { t } = useTranslation("lender-dashboard");

  const isEmpty = !isLoading && Number(count) === 0;

  const title = t(`summary.${type}.title`);

  const emptyText = t(`summary.${type}.short`);

  const helperText =
    helperType === "total"
      ? t("summary.common.helperTotalCount", {
          count: helperCount,
          defaultValue: "{{count}} total messages",
        })
      : t("summary.common.helperWithCount", {
          count: helperCount,
        });

  return (
    <article
      className={[
        "lender-dashboard__summary-card",
        `lender-dashboard__summary-card--${type}`,
        delayClass,
      ].join(" ")}
    >
      {isLoading ? (
        <div className="lender-dashboard__summary-loading">
          <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--title" />

          <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text" />

          <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text-short" />
        </div>
      ) : (
        <>
          <div className="lender-dashboard__summary-top">
            <div className="lender-dashboard__summary-copy">
              <h3 className="lender-dashboard__summary-title">{title}</h3>

              <p className="lender-dashboard__summary-subtitle">
                {isEmpty ? emptyText : label || emptyText}
              </p>
            </div>
          </div>

          <div className="lender-dashboard__summary-value-row">
            <div className="lender-dashboard__summary-value">{count}</div>

            <div className="lender-dashboard__summary-period">{helperText}</div>
          </div>

          <AnimatedSparkline tone={type} count={count} isEmpty={isEmpty} />
        </>
      )}
    </article>
  );
}
